import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { APP_ORIGIN, MAIN_ORIGIN } from '../lib/domain';

export interface Organization {
  id: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  org: Organization | null;
  orgLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, inviteToken?: string, companyName?: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: (inviteToken?: string) => Promise<void>;
  signInWithLinkedIn: (inviteToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshOrg: () => Promise<void>;
  consumePendingInvite: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Déconnexion volontaire en cours : la session tombe avant que le navigateur
// n'ait quitté la page, et AuthGuard renverrait sur /login. Ce drapeau lui dit
// de laisser la redirection vers la landing se faire.
let signingOut = false;
export const isSigningOut = () => signingOut;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState<Organization | null>(null);
  const [orgLoading, setOrgLoading] = useState(true);

  const fetchOrg = useCallback(async (userId: string) => {
    setOrgLoading(true);
    const { data, error } = await supabase
      .from('organization_members')
      .select('role, organizations(id, name)')
      .eq('user_id', userId)
      .order('joined_at', { ascending: true })
      .limit(1)
      .single();
    if (!error && data) {
      const o = data.organizations as any;
      setOrg({ id: o.id, name: o.name, role: data.role as Organization['role'] });
    } else {
      setOrg(null);
    }
    setOrgLoading(false);
  }, []);

  const refreshOrg = useCallback(async () => {
    const { data: { session: current } } = await supabase.auth.getSession();
    if (current?.user) await fetchOrg(current.user.id);
  }, [fetchOrg]);

  const consumePendingInvite = useCallback(async () => {
    const token = localStorage.getItem('invite_token');
    if (!token) return;
    const { data, error } = await supabase.rpc('accept_invite', { p_token: token });
    localStorage.removeItem('invite_token');
    const d = data as any;
    if (!error && d && !d.error) await refreshOrg();
  }, [refreshOrg]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      if (session?.user) {
        fetchOrg(session.user.id);
      } else {
        setOrgLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (session?.user) {
        fetchOrg(session.user.id);
        if (event === 'SIGNED_IN') {
          const token = localStorage.getItem('invite_token');
          if (token) {
            localStorage.removeItem('invite_token');
            supabase.rpc('accept_invite', { p_token: token });
          }
        }
      } else {
        setOrg(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchOrg]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string, inviteToken?: string, companyName?: string) => {
    const data: Record<string, string> = {};
    if (inviteToken) data.invite_token = inviteToken;
    if (companyName?.trim()) data.company_name = companyName.trim();
    const { data: signUpData, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: Object.keys(data).length > 0 ? data : undefined,
        emailRedirectTo: `${APP_ORIGIN}/onboarding`,
      },
    });
    // Supabase ne renvoie pas d'erreur pour un email déjà utilisé — on le détecte via identities vide
    if (!error && signUpData.user && signUpData.user.identities?.length === 0) {
      return { error: new Error('Un compte existe déjà avec cet email. Connectez-vous ou réinitialisez votre mot de passe.') };
    }
    return { error };
  };

  const signInWithGoogle = async (inviteToken?: string) => {
    if (inviteToken) localStorage.setItem('invite_token', inviteToken);
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${APP_ORIGIN}/onboarding` },
    });
  };

  const signInWithLinkedIn = async (inviteToken?: string) => {
    if (inviteToken) localStorage.setItem('invite_token', inviteToken);
    await supabase.auth.signInWithOAuth({
      provider: 'linkedin_oidc',
      options: { redirectTo: `${APP_ORIGIN}/onboarding` },
    });
  };

  const signOut = async () => {
    signingOut = true;
    try {
      await supabase.auth.signOut();
    } finally {
      // Landing publique (revertai.io en prod), rechargement complet pour
      // repartir sans aucun état applicatif en mémoire.
      window.location.href = `${MAIN_ORIGIN}/`;
    }
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, org, orgLoading, signIn, signUp, signInWithGoogle, signInWithLinkedIn, signOut, refreshOrg, consumePendingInvite }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
